/**
 * Resolver/normalizer for canonical {@link RootFilter} trees.
 *
 * Walks the LLM-facing filter AST and produces a {@link ResolvedRootFilter}:
 * every value-bearing leaf has its sourceRef materialized into a literal
 * shape. Literal leaves pass through unchanged — the resolved value shapes
 * are intentionally identical to the LLM-facing literal schemas
 * (`{ string }`, `{ number }`, `{ date }`, `{ array }`), with `{ ref }` /
 * `{ refs }` added for ref-typed projections. Downstream consumers
 * (evaluator, CDF translator) dispatch by field presence — no `kind`
 * envelope, no `??` fallbacks.
 *
 * Two concerns:
 *
 * 1. `sourceRef` projection. A `value: { inputDataset, column }` leaf is
 *    rewritten to a literal-shaped value populated from the referenced
 *    dataset column. The CDF query path (`queryKnowledgeGraphStep`) and the
 *    arquero path (`transformStep`) both call the resolver so chained
 *    filtering behaves identically across paths.
 *
 * 2. Literal pass-through. Pattern, equality, comparison, set, and null ops
 *    pass their literal payload straight through into the resolved tree.
 *
 * Failure modes:
 *  - Unknown `inputDataset` → `DatasetNotFoundError` (propagated).
 *  - Missing source column → `SourceRefColumnMissingError`.
 *  - Empty projection → `SourceRefEmptyError`.
 *  - Source/target type mismatch → `SourceRefTypeMismatchError`.
 *  - Non-set op with sourceRef projecting ≠1 row → `InvalidSpecError`.
 *  - Literal value provided for a ref-typed target column → `SourceRefTypeMismatchError`.
 */

import type z from 'zod';

import type { instanceReferenceSchema } from '#core/lib/cognite/types.ts';
import type { DatasetColumnType } from '#core/lib/transforms/column-schema.ts';

import type { DatasetResolver } from '../resolver/dataset-resolver.ts';
import type { ColumnSchema, Dataset } from '../types.ts';
import {
  COMPARISON_OPS,
  EQUALITY_OPS,
  type LeafFilter,
  type Level1Group,
  type LOGIC_OPS,
  type literalArraySchema,
  type literalDateSchema,
  type literalNumberSchema,
  type literalStringSchema,
  NULL_OPS,
  PATTERN_OPS,
  type RootFilter,
  SET_OPS,
} from './schema.ts';
import type { sourceRefSchema } from '#plugin-chat/lib/tool/dataset-keys.ts';
import {
  InvalidSpecError,
  SourceRefColumnMissingError,
  SourceRefEmptyError,
  SourceRefTypeMismatchError,
} from '#plugin-chat/lib/tool/errors/index.ts';

/** Virtual column name projecting a row's `{space, externalId}` as a ref value. */
export const VIRTUAL_INSTANCE_ID_COLUMN = '$instanceId';

/** Options accepted by {@link resolveFilterSourceRefs}. */
export interface ResolveSourceRefsOptions {
  /** Chat-session dataset resolver used to await upstream tool datasets. */
  resolve: DatasetResolver;
  /**
   * Resolves the canonical type of a filter's target column. Return
   * `undefined` when the type is unresolvable — the resolver will then skip
   * strict type-compatibility checks for that leaf.
   */
  getTargetType: (columnName: string) => DatasetColumnType | undefined;
  /** Step id used by callers for diagnostic logging. */
  stepId: string;
  /**
   * Human-readable context label for error messages (e.g. a view externalId,
   * or the string `"transformDataset"`). Used by callers to disambiguate
   * when the same error is surfaced from different step types.
   */
  contextLabel: string;
}

// Op-type aliases derived from the per-family tuples in `schema.ts`.
type EqualityOp = (typeof EQUALITY_OPS)[number];
type PatternOp = (typeof PATTERN_OPS)[number];
type ComparisonOp = (typeof COMPARISON_OPS)[number];
type SetOp = (typeof SET_OPS)[number];
type NullOp = (typeof NULL_OPS)[number];
type LogicOp = (typeof LOGIC_OPS)[number];

// Resolved value shapes — derived from the LLM-facing literal schemas in
// `schema.ts` (literal*Schema), augmented with `{ ref }` / `{ refs }` for
// resolved ref values. `Ref` is the canonical CDF instance reference type.
export type Ref = z.infer<typeof instanceReferenceSchema>;
type LiteralString = z.infer<typeof literalStringSchema>;
type LiteralNumber = z.infer<typeof literalNumberSchema>;
type LiteralDate = z.infer<typeof literalDateSchema>;
type LiteralArray = z.infer<typeof literalArraySchema>;

/** Single materialized value carried by `equals/notEquals` and comparison ops. */
export type ResolvedScalar = LiteralString | LiteralNumber | LiteralDate | { ref: Ref };
/** Materialized values carried by `in`/`notIn`. */
export type ResolvedArray = LiteralArray | { refs: Ref[] };

/**
 * Resolved leaf — same op families as `LeafFilter` but with materialized
 * values and no sourceRef shapes. Each variant uses the op-family tuples
 * from `schema.ts` so adding an op only requires updating that single tuple.
 */
export type ResolvedLeaf =
  | { column: string; op: EqualityOp; caseSensitive?: boolean; value: ResolvedScalar }
  | { column: string; op: PatternOp; caseSensitive?: boolean; literal: LiteralString }
  | { column: string; op: ComparisonOp; value: ResolvedScalar }
  | { column: string; op: SetOp; caseSensitive?: boolean; value: ResolvedArray }
  | { column: string; op: NullOp };

export type ResolvedLevel1Group = { type: LogicOp; filters: ResolvedLeaf[] };
export type ResolvedFilterNode = ResolvedLeaf | ResolvedLevel1Group;
export type ResolvedRootFilter = { type: LogicOp; filters: ResolvedFilterNode[] };

// Input value-shape unions — single source of truth is the Zod helpers in
// `schema.ts`. We re-derive TS types via `z.infer` so the resolver narrows
// against the same shapes the LLM-facing schema accepts.
type SourceRef = z.infer<typeof sourceRefSchema>;
type ScalarValue = LiteralString | LiteralNumber | LiteralDate | SourceRef;
type ArrayValue = LiteralArray | SourceRef;

/**
 * Rewrite a canonical {@link RootFilter} into a {@link ResolvedRootFilter} —
 * every leaf with `sourceRef` has its values materialized from a previously
 * staged dataset column; literal leaves pass through unchanged.
 *
 * Throws a {@link ToolExecutionError} subclass on resolution failure (see the
 * file docstring for the failure-mode taxonomy). The input is left untouched.
 */
export async function resolveFilterSourceRefs(
  filter: RootFilter,
  opts: ResolveSourceRefsOptions,
): Promise<ResolvedRootFilter> {
  const resolved = await rewriteGroup(filter, opts);
  return resolved as ResolvedRootFilter;
}

async function rewriteGroup(
  group: RootFilter | Level1Group,
  opts: ResolveSourceRefsOptions,
): Promise<ResolvedRootFilter | ResolvedLevel1Group> {
  const filters = await Promise.all(
    group.filters.map(async (node): Promise<ResolvedFilterNode> => {
      if ('op' in node) {
        return rewriteLeaf(node, opts);
      }
      return rewriteGroup(node, opts) as Promise<ResolvedLevel1Group>;
    }),
  );
  return { type: group.type, filters };
}

/** Op-family dispatch. The lookup map is derived from per-family tuples. */
async function rewriteLeaf(leaf: LeafFilter, opts: ResolveSourceRefsOptions): Promise<ResolvedLeaf> {
  return FAMILY_HANDLERS[OP_FAMILY[leaf.op]](leaf, opts);
}

export type OpFamily = 'null' | 'pattern' | 'equals' | 'comparison' | 'set';

/**
 * Op → family lookup. Derived from the per-family tuples in `schema.ts` so
 * adding a new op only requires updating the right tuple at the schema
 * source of truth — this map and FILTER_OPS pick it up automatically.
 */
export const OP_FAMILY: Record<LeafFilter['op'], OpFamily> = {
  ...assignFamily(NULL_OPS, 'null'),
  ...assignFamily(PATTERN_OPS, 'pattern'),
  ...assignFamily(EQUALITY_OPS, 'equals'),
  ...assignFamily(COMPARISON_OPS, 'comparison'),
  ...assignFamily(SET_OPS, 'set'),
};

function assignFamily<Op extends string, F extends OpFamily>(ops: readonly Op[], family: F): Record<Op, F> {
  return Object.fromEntries(ops.map((op) => [op, family])) as Record<Op, F>;
}

type FamilyHandler = (leaf: LeafFilter, opts: ResolveSourceRefsOptions) => Promise<ResolvedLeaf>;

const FAMILY_HANDLERS: Record<OpFamily, FamilyHandler> = {
  null: async (leaf) => ({ column: leaf.column, op: leaf.op as NullOp }),

  pattern: async (leaf) => {
    const l = leaf as Extract<LeafFilter, { literal: LiteralString; op: PatternOp }>;
    return { column: l.column, op: l.op, caseSensitive: l.caseSensitive, literal: l.literal };
  },

  equals: async (leaf, opts) => {
    const l = leaf as Extract<LeafFilter, { op: EqualityOp }>;
    return {
      column: l.column,
      op: l.op,
      caseSensitive: l.caseSensitive,
      value: await resolveScalar(l.value as ScalarValue, l.column, 'equality', opts),
    };
  },

  comparison: async (leaf, opts) => {
    const l = leaf as Extract<LeafFilter, { op: ComparisonOp }>;
    return {
      column: l.column,
      op: l.op,
      value: await resolveScalar(l.value as ScalarValue, l.column, 'comparison', opts),
    };
  },

  set: async (leaf, opts) => {
    const l = leaf as Extract<LeafFilter, { op: SetOp }>;
    return {
      column: l.column,
      op: l.op,
      caseSensitive: l.caseSensitive,
      value: await resolveArray(l.value as ArrayValue, l.column, opts),
    };
  },
};

// Scalar value resolution (equals/notEquals + comparison ops). Literals pass
// through unchanged — they already have the right resolved shape.

async function resolveScalar(
  value: ScalarValue,
  targetColumn: string,
  context: 'equality' | 'comparison',
  opts: ResolveSourceRefsOptions,
): Promise<ResolvedScalar> {
  if (isSourceRef(value)) {
    return projectScalarSourceRef(value, targetColumn, opts);
  }
  if ('string' in value) {
    rejectLiteralOnRefColumn(targetColumn, opts.getTargetType, 'string');
    return value;
  }
  if ('number' in value) {
    if (context === 'equality') {
      throw new InvalidSpecError(
        `equals/notEquals does not accept a {number} literal. Use eq/neq for numeric equality on column "${targetColumn}".`,
      );
    }
    rejectLiteralOnRefColumn(targetColumn, opts.getTargetType, 'number');
    return value;
  }
  if ('date' in value) {
    if (context === 'equality') {
      throw new InvalidSpecError(
        `equals/notEquals does not accept a {date} literal. Use eq for date equality on column "${targetColumn}".`,
      );
    }
    rejectLiteralOnRefColumn(targetColumn, opts.getTargetType, 'date');
    return value;
  }
  // Schema would have caught any other shape; this branch is unreachable.
  throw new InvalidSpecError(`Unrecognized literal value on column "${targetColumn}".`);
}

async function projectScalarSourceRef(
  ref: SourceRef,
  targetColumn: string,
  opts: ResolveSourceRefsOptions,
): Promise<ResolvedScalar> {
  const { values, sourceCanonical } = await projectFromDataset(ref, targetColumn, opts);
  if (values.length !== 1) {
    throw new InvalidSpecError(
      `sourceRef on column "${targetColumn}" projected ${values.length} rows; expected exactly 1 for a non-set comparison. Use the \`in\` op for multi-value membership.`,
    );
  }
  return scalarFromCanonical(sourceCanonical, values[0], targetColumn);
}

/** Wrap a projected value in the resolved shape that matches the source column type. */
function scalarFromCanonical(canonical: ColumnSchema['type'], value: unknown, targetColumn: string): ResolvedScalar {
  if (canonical === 'ref') {
    return { ref: value as Ref };
  }
  if (canonical === 'number') {
    return { number: value as number };
  }
  if (canonical === 'datetime') {
    return { date: typeof value === 'string' ? value : String(value) };
  }
  if (canonical === 'string' || canonical === 'unknown') {
    return { string: String(value) };
  }
  throw new SourceRefTypeMismatchError(targetColumn, 'string|number|datetime|ref', canonical);
}

// Array value resolution (in / notIn).

async function resolveArray(
  value: ArrayValue,
  targetColumn: string,
  opts: ResolveSourceRefsOptions,
): Promise<ResolvedArray> {
  if (isSourceRef(value)) {
    const { values, sourceCanonical } = await projectFromDataset(value, targetColumn, opts);
    if (sourceCanonical === 'ref') {
      return { refs: values as Ref[] };
    }
    return { array: values as (string | number | boolean)[] };
  }
  rejectLiteralOnRefColumn(targetColumn, opts.getTargetType, 'array');
  return value;
}

// SourceRef projection — shared between scalar and array paths.

function isSourceRef(value: unknown): value is SourceRef {
  return typeof value === 'object' && value !== null && 'inputDataset' in value && 'column' in value;
}

async function projectFromDataset(
  ref: SourceRef,
  targetColumn: string,
  opts: ResolveSourceRefsOptions,
): Promise<{ values: unknown[]; sourceCanonical: ColumnSchema['type'] }> {
  const { resolve, getTargetType, stepId, contextLabel } = opts;
  const dataset: Dataset = await resolve.byName(ref.inputDataset);
  const rawTargetType = getTargetType(targetColumn);
  const targetCanonical = rawTargetType === 'unknown' ? undefined : rawTargetType;

  const projected = projectSourceValues({
    dataset,
    inputDataset: ref.inputDataset,
    sourceColumn: ref.column,
    targetCanonical,
    targetColumnName: targetColumn,
    contextLabel,
    stepId,
  });

  if (projected.values.length === 0) {
    throw new SourceRefEmptyError(ref.column);
  }

  // Type compatibility check — only enforced when both sides have known types.
  // The staged dataset always carries a ColumnSchema; the target type map may
  // be empty in tests or for synthetic columns, in which case we trust the
  // projection and skip the match.
  if (
    targetCanonical !== undefined &&
    projected.sourceCanonical !== 'unknown' &&
    targetCanonical !== projected.sourceCanonical
  ) {
    throw new SourceRefTypeMismatchError(targetColumn, targetCanonical, projected.sourceCanonical);
  }

  return projected;
}

interface ProjectSourceValuesArgs {
  dataset: Dataset;
  inputDataset: string;
  sourceColumn: string;
  targetCanonical: DatasetColumnType | undefined;
  targetColumnName: string;
  contextLabel: string;
  stepId: string;
}

function projectSourceValues(args: ProjectSourceValuesArgs): {
  values: unknown[];
  sourceCanonical: ColumnSchema['type'];
} {
  const { dataset, sourceColumn, targetCanonical, targetColumnName } = args;

  if (sourceColumn === VIRTUAL_INSTANCE_ID_COLUMN) {
    if (targetCanonical !== undefined && targetCanonical !== 'ref') {
      throw new SourceRefTypeMismatchError(targetColumnName, 'ref', targetCanonical);
    }
    const values = dataset.rows
      .map((row) => {
        const space = row.space;
        const externalId = row.externalId;
        if (typeof space !== 'string' || typeof externalId !== 'string') {
          return undefined;
        }
        return { space, externalId };
      })
      .filter((v): v is Ref => v !== undefined);
    return { values, sourceCanonical: 'ref' };
  }

  const colSchema = dataset.columns.find((c) => c.name === sourceColumn);
  if (!colSchema) {
    throw new SourceRefColumnMissingError(
      sourceColumn,
      dataset.columns.map((c) => c.name),
    );
  }

  const values = dataset.rows.map((row) => row[sourceColumn]).filter((v) => v !== null && v !== undefined);
  return { values, sourceCanonical: colSchema.type };
}

/**
 * Reject a literal value on a ref-typed target column. Spaces and externalIds
 * vary by view/tenant and must come from a real query result — sourceRef is
 * the only legitimate path.
 */
function rejectLiteralOnRefColumn(
  targetColumn: string,
  getTargetType: (columnName: string) => DatasetColumnType | undefined,
  literalKind: string,
): void {
  if (getTargetType(targetColumn) === 'ref') {
    throw new SourceRefTypeMismatchError(targetColumn, 'ref (use sourceRef)', literalKind);
  }
}

/**
 * Structural equality for CDF instance references. Two refs match when both
 * sides are objects carrying matching `space` and `externalId` strings.
 * Object identity (`===`) is not reliable because refs projected from
 * different tool calls / datasets are structurally equal but not
 * reference-equal.
 */
export function refEquals(a: unknown, b: unknown): boolean {
  if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') {
    return false;
  }
  const refA = a as { space?: unknown; externalId?: unknown };
  const refB = b as { space?: unknown; externalId?: unknown };
  return (
    typeof refA.space === 'string' &&
    typeof refA.externalId === 'string' &&
    refA.space === refB.space &&
    refA.externalId === refB.externalId
  );
}
